import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, TrendingDown, TrendingUp, CreditCard, RefreshCw, CheckCircle, XCircle, Loader2, Settings, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';

const PACKS = [
  { id: 'standard', credits: 500,  price: '$9.99',  label: 'Standard', per: '$0.020/cr', highlight: true  },
  { id: 'pro',      credits: 750,  price: '$19.99', label: 'Pro',      per: '$0.027/cr', highlight: false },
  { id: 'studio',   credits: 1250, price: '$49.99', label: 'Studio',   per: '$0.040/cr', highlight: false },
];

const SUBSCRIPTION = {
  id: 'pro_monthly',
  name:    'Pro Monthly',
  price:   '$9.99/mo',
  credits: 200,
  rollover: 100,
  description: '200 credits/month + roll over up to 100 unused credits. Unlocks the desktop analyser and web uploads.',
};

const ACTION_LABELS = {
  upload:              'Upload',
  analyze:             'Analysis',
  chat_message:        'DJ Coach chat',
  download_m3u8:       'M3U8 export',
  download_mp3:        'MP3 download',
  ai_set_generation:   'AI set generation',
  welcome_bonus:       'Welcome bonus',
  admin_grant:         'Admin grant',
  stripe_purchase:     'Credit pack purchase',
  monthly_subscription:'Subscription',
};

export default function BillingPage() {
  const { user, subscription, refreshSubscription } = useAuth();
  const location    = useLocation();
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [buyingPack, setBuyingPack]     = useState(null);   // pack id being purchased
  const [subscribing, setSubscribing]   = useState(false);  // Pro Monthly checkout in flight
  const [managing, setManaging]         = useState(false);  // portal redirect in flight
  const [pollState, setPollState]       = useState(null);   // 'polling' | 'success' | 'failed'
  const [pollCredits, setPollCredits]   = useState(null);   // credits gained

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/credits/balance`);
      setData(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Handle Stripe redirect back (session_id in URL) ──────────────────────
  useEffect(() => {
    const params        = new URLSearchParams(location.search);
    const sessionId     = params.get('session_id');
    const subSessionId  = params.get('subscription_session');
    if (!sessionId && !subSessionId) return;

    // Clean the URL without reload
    window.history.replaceState({}, '', '/billing');
    setPollState('polling');

    // ── Subscription verification path ──────────────────────────────────
    if (subSessionId) {
      let attempts = 0;
      const MAX    = 10;
      const DELAY  = 2000;
      const verify = async () => {
        try {
          const r = await axios.post(`${API}/subscriptions/verify-session/${subSessionId}`);
          if (r.data.ready) {
            setPollCredits(r.data.credits);
            setPollState('success');
            toast.success('Welcome to CRATELAB Pro! Uploads and analysis are unlocked.');
            await refreshSubscription();
            await load();
            return;
          }
        } catch (err) {
          if (attempts >= MAX - 1) {
            setPollState('failed');
            toast.error('Could not confirm subscription. Contact support if charged.');
            return;
          }
        }
        attempts++;
        if (attempts < MAX) {
          setTimeout(verify, DELAY);
        } else {
          setPollState('failed');
          toast.error('Subscription confirmation timed out. Contact support if charged.');
        }
      };
      verify();
      return;
    }

    // ── One-off credit pack path (existing) ─────────────────────────────
    let attempts = 0;
    const MAX    = 8;
    const DELAY  = 2500;

    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
        const { payment_status, credits } = r.data;

        if (payment_status === 'paid') {
          setPollCredits(credits);
          setPollState('success');
          toast.success(`${credits} credits added to your account!`);
          await load();
          return;
        }
        if (r.data.status === 'expired') {
          setPollState('failed');
          toast.error('Payment session expired. Please try again.');
          return;
        }
      } catch (err) {
        if (attempts >= MAX - 1) {
          setPollState('failed');
          toast.error('Could not confirm payment. Contact support if credits are missing.');
          return;
        }
      }
      attempts++;
      if (attempts < MAX) {
        setTimeout(poll, DELAY);
      } else {
        setPollState('failed');
        toast.error('Payment confirmation timed out. Contact support if credits are missing.');
      }
    };

    poll();
  }, [location.search, load, refreshSubscription]);

  // ── Buy a credit pack ─────────────────────────────────────────────────────
  const buyPack = async (packId) => {
    setBuyingPack(packId);
    try {
      const r = await axios.post(`${API}/payments/create-checkout`, {
        pack_id:    packId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBuyingPack(null);
    }
  };

  // ── Subscribe to Pro Monthly ─────────────────────────────────────────────
  const subscribeMonthly = async () => {
    setSubscribing(true);
    try {
      const r = await axios.post(`${API}/subscriptions/create-checkout`, {
        plan_id:    'pro_monthly',
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || detail || 'Could not start subscription checkout.');
      setSubscribing(false);
    }
  };

  // ── Manage subscription (Stripe portal) ──────────────────────────────────
  const openPortal = async () => {
    setManaging(true);
    try {
      const r = await axios.post(`${API}/subscriptions/portal`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open subscription portal.');
      setManaging(false);
    }
  };

  const balance = data?.credits ?? 0;
  const txns    = data?.transactions ?? [];
  const costs   = data?.costs ?? {};
  const low     = balance < 10;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Billing</h1>
        <p className="font-body text-sm text-[#64748B] mt-1">Manage your credits, top up, and view transaction history.</p>
      </div>

      {/* Payment status banner */}
      <AnimatePresence>
        {pollState && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`mb-6 flex items-center gap-3 px-5 py-4 border rounded-xl ${
              pollState === 'polling' ? 'bg-blue-50 border-blue-200' :
              pollState === 'success' ? 'bg-emerald-50 border-emerald-200' :
              'bg-red-50 border-red-200'
            }`}
            data-testid="payment-status-banner"
          >
            {pollState === 'polling' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />}
            {pollState === 'success' && <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
            {pollState === 'failed'  && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
            <div>
              {pollState === 'polling' && <p className="font-body text-sm text-blue-700">Confirming your payment…</p>}
              {pollState === 'success' && (
                <p className="font-body text-sm text-emerald-700">
                  Payment confirmed — <span className="font-bold">{pollCredits} credits</span> added to your account.
                </p>
              )}
              {pollState === 'failed' && (
                <p className="font-body text-sm text-red-700">
                  Payment confirmation failed. If you were charged, contact{' '}
                  <a href="mailto:admin@cratelab.io" className="underline">admin@cratelab.io</a>.
                </p>
              )}
            </div>
            {pollState !== 'polling' && (
              <button onClick={() => setPollState(null)} className="ml-auto text-xs text-[#64748B] hover:text-[#0F172A]">✕</button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Balance hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl p-6 mb-6 border ${low ? 'bg-amber-50 border-amber-200' : 'bg-gradient-to-br from-[#EFF6FF] to-[#F0FDFA] border-[#BFDBFE]'}`}
        data-testid="billing-balance-hero"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-body text-sm text-[#64748B] mb-1">Current balance</p>
            <p className={`font-heading text-5xl font-bold ${low ? 'text-amber-600' : 'text-[#2563FF]'}`} data-testid="billing-credit-balance">
              {loading ? '—' : balance}
            </p>
            <p className="font-body text-sm text-[#64748B] mt-1">credits</p>
            {low && <p className="font-body text-xs text-amber-600 mt-2">Running low — top up to continue using uploads, analysis, and AI sets.</p>}
          </div>
          <button
            onClick={load}
            className="p-2.5 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/40 transition-colors self-start"
            data-testid="billing-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 text-[#64748B]" />
          </button>
        </div>
      </motion.div>

      {/* Credit cost reference */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-6">
        <h3 className="font-heading text-sm text-[#0F172A] mb-4">Credit costs</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: 'Upload (per file)', value: '1–4 cr', sub: 'based on file size' },
            { label: 'Analysis',          value: `${costs.analyze ?? 2} cr`,      sub: 'per track' },
            { label: 'DJ Coach message',  value: `${costs.chat_message ?? 1} cr`, sub: 'per message' },
            { label: 'AI set generation', value: `${costs.ai_set_generation ?? 5} cr`, sub: 'per set' },
            { label: 'M3U8 export',       value: `${costs.download_m3u8 ?? 1} cr`,    sub: 'per playlist' },
            { label: 'MP3 download (S)',  value: `${costs.download_mp3_small ?? 2} cr`,  sub: '1–5 tracks' },
            { label: 'MP3 download (M)',  value: `${costs.download_mp3_medium ?? 4} cr`, sub: '6–15 tracks' },
            { label: 'MP3 download (L)',  value: `${costs.download_mp3_xl ?? 8} cr`,     sub: '31+ tracks' },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-[#F5F7FB] rounded-xl p-3 text-center">
              <p className="font-mono text-sm font-bold text-[#2563FF]">{value}</p>
              <p className="font-body text-[10px] text-[#0F172A] mt-0.5">{label}</p>
              <p className="font-body text-[9px] text-[#94A3B8]">{sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Credit packs */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-6">
        <h3 className="font-heading text-sm text-[#0F172A] mb-4">Top up — credit packs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PACKS.map(pack => (
            <div
              key={pack.id}
              className={`relative rounded-xl border p-4 text-center transition-all flex flex-col
                ${pack.highlight
                  ? 'bg-gradient-to-b from-[#EFF6FF] to-[#F5F7FB] border-[#2563FF]/40 shadow-[0_4px_12px_rgba(37,99,255,0.08)]'
                  : 'bg-white border-[#E2E8F0]'
                }`}
              data-testid={`billing-pack-${pack.id}`}
            >
              {pack.highlight && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-heading bg-[#2563FF] text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                  Popular
                </span>
              )}
              <p className="font-heading text-2xl font-bold text-[#0F172A]">{pack.credits}</p>
              <p className="font-body text-[10px] text-[#94A3B8] mb-2">credits</p>
              <p className="font-heading text-base text-[#2563FF]">{pack.price}</p>
              <p className="font-body text-[9px] text-[#94A3B8] mt-0.5">{pack.per}</p>
              <p className="font-mono text-[10px] text-[#64748B] mt-1 mb-3">{pack.label}</p>

              <button
                onClick={() => buyPack(pack.id)}
                disabled={!!buyingPack || pollState === 'polling'}
                className={`mt-auto w-full py-2 rounded-lg text-sm font-heading transition-all flex items-center justify-center gap-1.5
                  ${pack.highlight
                    ? 'bg-[#2563FF] text-white hover:bg-[#1D4ED8] disabled:opacity-60'
                    : 'bg-[#F5F7FB] text-[#0F172A] border border-[#E2E8F0] hover:border-[#2563FF]/50 hover:text-[#2563FF] disabled:opacity-60'
                  }`}
                data-testid={`buy-pack-${pack.id}`}
              >
                {buyingPack === pack.id
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</>
                  : <><Zap className="w-3.5 h-3.5" /> Buy</>
                }
              </button>
            </div>
          ))}
        </div>

        <p className="font-body text-[10px] text-[#94A3B8] text-center mt-3">
          Payments processed securely by Stripe. No card details stored by CRATELAB.
        </p>
      </div>

      {/* Pro Monthly Subscription */}
      <div
        className={`relative rounded-2xl p-5 mb-6 border-2 ${
          subscription?.active
            ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-300'
            : 'bg-gradient-to-br from-[#EFF6FF] to-white border-[#2563FF]/40 shadow-[0_4px_20px_rgba(37,99,255,0.08)]'
        }`}
        data-testid="billing-subscription-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className={`w-4 h-4 ${subscription?.active ? 'text-emerald-500' : 'text-[#2563FF]'}`} />
              <h3 className="font-heading text-base text-[#0F172A]">{SUBSCRIPTION.name}</h3>
              {subscription?.active && (
                <span className="text-[10px] font-heading bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
              {subscription?.status === 'past_due' && (
                <span className="text-[10px] font-heading bg-amber-500 text-white px-2 py-0.5 rounded-full">
                  Past due
                </span>
              )}
            </div>
            <p className="font-body text-xs text-[#64748B] mb-2">{SUBSCRIPTION.description}</p>
            {subscription?.active && subscription.period_end && (
              <p className="font-mono text-[11px] text-[#0F172A]" data-testid="billing-subscription-renews">
                Renews {new Date(subscription.period_end).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })}
                {' · '}{subscription.subscription_credits_current ?? 0} sub-credits remaining this cycle
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="font-heading text-2xl font-bold text-[#2563FF] mb-2">{SUBSCRIPTION.price}</p>
            {subscription?.active ? (
              <button
                onClick={openPortal}
                disabled={managing}
                className="px-4 py-2 rounded-lg bg-white border border-[#E2E8F0] text-sm font-heading text-[#0F172A] hover:border-[#2563FF]/50 hover:text-[#2563FF] transition-all inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="billing-manage-subscription-btn"
              >
                {managing
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening…</>
                  : <><Settings className="w-3.5 h-3.5" /> Manage subscription</>
                }
              </button>
            ) : (
              <button
                onClick={subscribeMonthly}
                disabled={subscribing || pollState === 'polling'}
                className="px-5 py-2 rounded-lg bg-[#2563FF] text-white text-sm font-heading hover:bg-[#1D4ED8] transition-all inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="billing-subscribe-btn"
              >
                {subscribing
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</>
                  : <><Zap className="w-3.5 h-3.5" /> Subscribe</>
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Transaction history */}
      {txns.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <h3 className="font-heading text-sm text-[#0F172A] mb-4">Transaction history</h3>
          <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
            {txns.map(tx => (
              <div
                key={tx.id}
                className="flex items-center gap-3 px-3 py-2.5 bg-[#F5F7FB] rounded-lg"
                data-testid={`billing-txn-${tx.id}`}
              >
                {tx.type === 'credit'
                  ? <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  : <TrendingDown className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className="font-body text-xs text-[#0F172A] truncate">
                    {ACTION_LABELS[tx.action] ?? tx.action}
                  </p>
                  {tx.description && (
                    <p className="font-body text-[10px] text-[#94A3B8] truncate">{tx.description}</p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-mono text-xs font-bold ${tx.type === 'credit' ? 'text-emerald-600' : 'text-red-500'}`}>
                    {tx.type === 'credit' ? '+' : ''}{tx.amount}
                  </p>
                  <p className="font-mono text-[9px] text-[#94A3B8]">{tx.balance_after} left</p>
                </div>
                <p className="font-mono text-[10px] text-[#94A3B8] hidden sm:block w-20 text-right flex-shrink-0">
                  {new Date(tx.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {txns.length === 0 && !loading && (
        <div className="text-center py-10">
          <CreditCard className="w-10 h-10 text-[#E2E8F0] mx-auto mb-3" />
          <p className="font-body text-sm text-[#94A3B8]">No transactions yet.</p>
        </div>
      )}
    </div>
  );
}
